import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { getStatusConfig } from '../utils/bookingStatus';
import {
  clipToWindow,
  isSlotInWindow,
  timeToMinutes,
  minutesToTime,
  slotStarts,
  coversMinute,
  isBusinessMinute,
  breakBands,
  localIsoDate,
  BREAK_HATCHING,
  CLASSIC_SLOT_HEIGHT,
  SLOT_MINUTES,
} from '../utils/scheduleUtils';

const HOUR_HEIGHT_PX = CLASSIC_SLOT_HEIGHT * 2;

// Shared by a column and its header, which sit in different rows of the board (see
// ScheduleScroller) and must stay exactly as wide as each other. `boxSizing` included: as flex
// items side by side, a 1px border would otherwise count on one and not the other.
const dayColumnSx = {
  minWidth: 260,
  flex: '0 0 260px',
  boxSizing: 'border-box',
  borderRight: '1px solid #e0e0e0',
  '&:last-child': { borderRight: 'none' },
};

const minutesToTop = (minutes, windowStart) => ((minutes - windowStart) / 60) * HOUR_HEIGHT_PX;

const durationToHeight = (startMinutes, endMinutes) =>
  Math.max(((endMinutes - startMinutes) / 60) * HOUR_HEIGHT_PX, 22);

/** Interval-graph layout: overlapping bookings share the column width. */
const computeLayout = (timeSlots) => {
  const sorted = [...timeSlots].sort(
    (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  );
  const columns = [];
  const slotMeta = sorted.map((slot) => {
    const start = timeToMinutes(slot.startTime);
    const end = timeToMinutes(slot.endTime);
    let col = columns.findIndex((c) => c.endMinutes <= start);
    if (col === -1) {
      col = columns.length;
      columns.push({ endMinutes: end });
    } else {
      columns[col].endMinutes = end;
    }
    return { slot, col };
  });
  return slotMeta.map(({ slot, col }) => {
    const start = timeToMinutes(slot.startTime);
    const end = timeToMinutes(slot.endTime);
    const totalCols = slotMeta.filter(({ slot: s }) => {
      const sStart = timeToMinutes(s.startTime);
      const sEnd = timeToMinutes(s.endTime);
      return sStart < end && sEnd > start;
    }).length;
    return { slot, col, totalCols };
  });
};

const BookingBlock = ({ slot, col, totalCols, gridWindow, onClick }) => {
  const config = getStatusConfig(slot.bookingStatus);
  // Only the part on the grid is drawn; the label still gives the booking's real times.
  const visible = clipToWindow(gridWindow, timeToMinutes(slot.startTime), timeToMinutes(slot.endTime));
  const top = minutesToTop(visible.start, gridWindow.start);
  const height = durationToHeight(visible.start, visible.end);
  const isShort = height < 41;
  const widthPct = 100 / totalCols;
  const leftPct = col * widthPct;
  const StatusIcon = config.Icon;

  return (
    <Box
      data-testid="schedule-booking"
      data-booking-id={slot.id}
      data-status={slot.bookingStatus}
      onClick={() => onClick && onClick(slot.id)}
      sx={{
        position: 'absolute',
        top: top + 5,
        left: `calc(${leftPct}% + 3px)`,
        width: `calc(${widthPct}% - 6px)`,
        height: height - 10,
        bgcolor: config.bg,
        border: `1.5px solid ${config.border}`,
        borderRadius: '6px',
        px: 1,
        py: 0.75,
        cursor: 'pointer',
        overflow: 'hidden',
        zIndex: 1,
        display: 'flex',
        flexDirection: isShort ? 'row' : 'column',
        alignItems: isShort ? 'center' : 'flex-start',
        gap: 0.4,
        boxSizing: 'border-box',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': {
          boxShadow: `0 2px 8px ${config.border}66`,
          transform: 'translateY(-1px)',
          zIndex: 2,
        },
      }}
    >
      <Chip
        icon={React.createElement(StatusIcon, { sx: { fontSize: 11 } })}
        label={isShort ? '' : config.label}
        size="small"
        sx={{
          bgcolor: config.chipBg,
          color: '#fff',
          fontSize: '10px',
          height: '18px',
          fontWeight: 600,
          flexShrink: 0,
          '& .MuiChip-icon': { color: '#fff', ml: '4px' },
          '& .MuiChip-label': { px: isShort ? '2px' : '5px' },
        }}
      />
      {!isShort && (
        <React.Fragment>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <PersonIcon sx={{ fontSize: 11, color: '#555', flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: '11px', color: '#333', fontWeight: 500, lineHeight: 1.3 }}>
              {slot.guestName}
            </Typography>
          </Box>
          {slot.supplyName && (
            <Typography noWrap sx={{ fontSize: '10px', color: '#555', fontStyle: 'italic', lineHeight: 1.3, pl: '15px' }}>
              {slot.supplyName}
            </Typography>
          )}
          <Typography sx={{ fontSize: '10px', color: '#666', lineHeight: 1, pl: '15px' }}>
            {slot.startTime}–{slot.endTime}
          </Typography>
        </React.Fragment>
      )}
    </Box>
  );
};

const GridLines = ({ hourCount, currentTimeTop }) => (
  <React.Fragment>
    {Array.from({ length: hourCount + 1 }, (_, i) => (
      <Box
        key={i}
        sx={{ position: 'absolute', top: i * HOUR_HEIGHT_PX, left: 0, right: 0, borderTop: '1px solid #d8d8d8', zIndex: 0 }}
      />
    ))}
    {Array.from({ length: hourCount }, (_, i) => (
      <Box
        key={`h-${i}`}
        sx={{
          position: 'absolute',
          top: i * HOUR_HEIGHT_PX + HOUR_HEIGHT_PX / 2,
          left: 0,
          right: 0,
          borderTop: '1px solid #eeeeee',
          zIndex: 0,
        }}
      />
    ))}
    {currentTimeTop !== null && (
      <Box
        sx={{
          position: 'absolute',
          top: currentTimeTop,
          left: 0,
          right: 0,
          borderTop: '2px solid #f4433650',
          zIndex: 3,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-5px',
            left: 0,
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#f44336',
          },
        }}
      />
    )}
  </React.Fragment>
);

export const DailyScheduleCard = ({
  dailySchedule,
  gridWindow,
  onBookedSlotClick,
  onFreeSlotClick,
}) => {
  const isToday = dailySchedule.date === localIsoDate();

  const currentMinutes = () => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  };

  const currentTopIfVisible = () => {
    const minutes = currentMinutes();
    if (minutes < gridWindow.start || minutes >= gridWindow.end) return null;
    return minutesToTop(minutes, gridWindow.start);
  };

  const [currentTimeTop, setCurrentTimeTop] = useState(isToday ? currentTopIfVisible() : null);

  useEffect(() => {
    if (!isToday) return undefined;

    let interval;
    const tick = () => setCurrentTimeTop(currentTopIfVisible());
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const timeout = setTimeout(() => {
      tick();
      interval = setInterval(tick, 60_000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, gridWindow.start, gridWindow.end]);

  const timeSlots = dailySchedule.timeSlots || [];
  // Bookings in hidden hours stay out of the layout too, so they take no width from the others.
  const layout = computeLayout(timeSlots.filter((slot) => isSlotInWindow(gridWindow, slot)));

  // A non-resource tenant serves one guest at a time, so any covering booking takes the slot.
  // The resource board answers the same question from remaining capacity instead.
  const freeSlotZones = onFreeSlotClick
    ? slotStarts(gridWindow)
        .map((minutes, index) => {
          if (timeSlots.some((slot) => coversMinute(slot, minutes))) return null;

          const startTime = minutesToTime(minutes);
          const endTime = minutesToTime(minutes + SLOT_MINUTES);

          return (
            <Box
              key={`free-${minutes}`}
              onClick={() => onFreeSlotClick(dailySchedule.date, { startTime, endTime })}
              sx={{
                position: 'absolute',
                top: index * CLASSIC_SLOT_HEIGHT,
                left: 0,
                right: 0,
                height: CLASSIC_SLOT_HEIGHT,
                zIndex: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'transparent',
                fontSize: '11px',
                fontWeight: 500,
                transition: 'background 0.15s, color 0.15s',
                '&:hover': { background: 'rgba(25, 118, 210, 0.06)', color: '#2196f3' },
              }}
            >
              + {startTime}
            </Box>
          );
        })
        .filter(Boolean)
    : [];

  return (
    <Box data-testid="schedule-day" data-date={dailySchedule.date} sx={dayColumnSx}>
      <Box sx={{ position: 'relative', height: gridWindow.slotCount * CLASSIC_SLOT_HEIGHT, bgcolor: '#fafafa' }}>
        {/* Outside opening hours: still bookable by an admin, but visibly not business time. */}
        {slotStarts(gridWindow)
          .map((minutes, index) =>
            isBusinessMinute(gridWindow, minutes, dailySchedule.date) ? null : (
              <Box
                key={`closed-${minutes}`}
                sx={{
                  position: 'absolute',
                  top: index * CLASSIC_SLOT_HEIGHT,
                  left: 0,
                  right: 0,
                  height: CLASSIC_SLOT_HEIGHT,
                  bgcolor: '#f0f0f2',
                  zIndex: 0,
                }}
              />
            )
          )
          .filter(Boolean)}
        {/* The tenant's daily breaks (D32): no guest can book into one, an admin still can, so
            the band is shading and lets every click through to the free zone under it. */}
        {breakBands(gridWindow, dailySchedule.date).map((band) => (
          <Box
            key={`break-${band.start}`}
            data-testid="schedule-break"
            sx={{
              position: 'absolute',
              top: minutesToTop(band.start, gridWindow.start),
              left: 0,
              right: 0,
              height: ((band.end - band.start) / 60) * HOUR_HEIGHT_PX,
              backgroundImage: BREAK_HATCHING,
              zIndex: 0,
              pointerEvents: 'none',
              display: 'flex',
              justifyContent: 'center',
              pt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#8a8a94', letterSpacing: '0.04em' }}>
              SZÜNET
            </Typography>
          </Box>
        ))}
        <GridLines hourCount={Math.ceil(gridWindow.slotCount / 2)} currentTimeTop={currentTimeTop} />
        {freeSlotZones}
        {layout.map((item) => (
          <BookingBlock
            key={item.slot.id}
            slot={item.slot}
            col={item.col}
            totalCols={item.totalCols}
            gridWindow={gridWindow}
            onClick={onBookedSlotClick}
          />
        ))}
      </Box>
    </Box>
  );
};

/** A column's header, in the board's header row rather than in the column itself. */
export const DayHeader = ({ dailySchedule }) => {
  const isToday = dailySchedule.date === localIsoDate();

  return (
    <Box
      sx={{
        ...dayColumnSx,
        bgcolor: '#1a1a1a',
        px: 1.5,
        height: '38px',
        borderBottom: isToday ? '2px solid #2196f3' : '2px solid transparent',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#fff', textTransform: 'capitalize' }}>
        {dailySchedule.weekday}
      </Typography>
      <Typography sx={{ fontSize: '11px', color: '#aaa', whiteSpace: 'nowrap' }}>
        {dailySchedule.formattedDate}
      </Typography>
    </Box>
  );
};

export default DailyScheduleCard;
